import { Component, computed, effect, Input, OnDestroy, OnInit, Renderer2, Signal, signal, WritableSignal } from '@angular/core';
import { Subscription } from 'rxjs';
import { color, ECharts, graphic, } from 'echarts';
import langEN from 'echarts/lib/i18n/langEN.js';
import langFR from 'echarts/lib/i18n/langFR.js';
import langDE from 'echarts/lib/i18n/langDE.js';
import { SegmentPoint } from '../../models/segment-point';
import { ProfilePathService } from '../../services/profile-path.service';
import { ElevationService } from '../../services/elevation.service';
import { ToggleEventNameType, ToggleEventService, ToggleEventType } from '../../services/toggle-event.service';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../services/language.service';
import { MapService } from '../../services/map.service';
import { TraceRouteDataUpdateType, TraceService } from '../../services/trace.service';
import { BrowserSnifferService } from '../../services/browser-sniffer.service';
import { Point } from 'ol/geom';
import { ElevationPoiType } from '../../models/elevation-poi';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@tt-lib/material.module';
import { NgxEchartsModule } from 'ngx-echarts';
import { BackEventService } from '@tt-lib/services/back-event.service';

// // Import the echarts core module, which provides the necessary interfaces for using echarts.
// import * as echarts from 'echarts/core';

// // Import bar charts, all suffixed with Chart
// import { BarChart } from 'echarts/charts';

// // Import the tooltip, title, rectangular coordinate system, dataset and transform components
// import {
//   TitleComponent,
//   TooltipComponent,
//   GridComponent,
//   ToolboxComponent,
//   DatasetComponent,
//   TransformComponent
// } from 'echarts/components';

// // Features like Universal Transition and Label Layout
// import { LabelLayout, UniversalTransition } from 'echarts/features';

// // Import the Canvas renderer
// // Note that including the CanvasRenderer or SVGRenderer is a required step
// import { SVGRenderer } from 'echarts/renderers';

// // Register the required components
// // echarts.use([
// //   BarChart,
// //   TitleComponent,
// //   TooltipComponent,
// //   ToolboxComponent,
// //   GridComponent,
// //   LabelLayout,
// //   CanvasRenderer
// // ]);


@Component({
  selector: 'tt-elevation-profile',
  imports: [
    CommonModule,
    MaterialModule,
    TranslateModule,
    NgxEchartsModule
  ],
  templateUrl: './elevation-profile.component.html',
  styleUrls: ['./elevation-profile.component.scss'],
  standalone: true
})

export class ElevationProfileComponent implements OnInit, OnDestroy {
  private _mapElement: HTMLElement;
  private _elevationProfileElement: HTMLElement;
  private _olControlElements: NodeListOf<HTMLElement>;
  public ref_hasRouteToCreateProfile: WritableSignal<boolean> = signal(false);
  private isDarkTheme: boolean;
  private visibleRouteLength: number;
  private isZoomed: boolean = false;

  points: SegmentPoint[];
  selectedPointId: number;
  hoveredPointId: number;
  options: {};
  mergeOptions: WritableSignal<any> = signal({});
  isChartShown: WritableSignal<boolean> = signal(false);
  summary: string = "";

  data = {
    xData: [],
    yData: [],
    lats: [],
    longs: [],
    ids: [],
    slopes: [],
    gradients: [],
    poiData: []
  }

  @Input() autoCloseOnNoData: boolean = true;
  @Input() autoOpenOnData: boolean = true;
  @Input() autoDistanceUnit: boolean = true;//if false, always in meters
  private distanceUnit: 'm' | 'km' = 'm';
  private subscriptions: Subscription;
  private echartsInstance: ECharts;
  // public echartsInstance;
  private echartLocale: string;
  private maxElevation: number = 0;

  private isComputingPoints: WritableSignal<boolean> = signal(false);
  private isBuildingProfileFromPoints: WritableSignal<boolean> = signal(false);
  isBuildingProfile: Signal<boolean> = computed(() => this.isComputingPoints() || this.isBuildingProfileFromPoints());

  private isDrawingToolsOpen: Boolean
  private ngiOrangeColor = 'rgba(234, 89, 1, 1.0)';
  private ngiOrangeColorDarker = 'rgba(225, 62, 1, 1.0)';
  private ngiOrangeColorVeryDark = 'rgba(141, 38, 0, 1.0)';
  private ngiDarkBlueColor = 'rgba(44, 60, 86, 1.0)';
  private ngiBlueColor = 'rgba(0, 108, 157, 1.0)';
  private ngiBlueGreenColor = 'rgba(6, 138, 155, 1.0)';
  private ngiGreenBlueColor = 'rgba(47, 162, 152, 1.0)';
  private ngiGrayColor = 'rgba(194, 194, 194, 1.0)';
  private ngiDarkGrayColor = 'rgba(97, 97, 97, 1.0)';
  // private myRestoreIcon = 'path://M12,21c-2.3,0-4.3-0.8-6-2.3S3.3,15.3,3,13h2c0.2,1.7,1,3.2,2.3,4.3S10.2,19,12,19c1.9,0,3.6-0.7,5-2s2-3,2-5s-0.7-3.6-2-5s-3-2-5-2c-1.1,0-2.2,0.3-3.2,0.8S6.9,7.1,6.2,8H9v2H3V4h2v2.3c0.8-1.1,1.9-1.9,3.1-2.5S10.6,3,12,3c1.2,0,2.4,0.2,3.5,0.7s2,1.1,2.9,1.9c0.8,0.8,1.4,1.8,1.9,2.8S21,10.7,21,12s-0.2,2.4-0.7,3.5s-1.1,2-1.9,2.8c-0.8,0.8-1.8,1.5-2.9,1.9C14.4,20.8,13.2,21,12,21z';
  private myRestoreIcon = 'path://M480-80q-75 0-140.5-28.5t-114-77q-48.5-48.5-77-114T120-440h80q0 117 81.5 198.5T480-160q117 0 198.5-81.5T760-440q0-117-81.5-198.5T480-720h-6l62 62-56 58-160-160 160-160 56 58-62 62h6q75 0 140.5 28.5t114 77q48.5 48.5 77 114T840-440q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T480-80Z';
  private dataZoomIcon = 'path://M38.7,38c0,0.5-0.2,1-0.6,1.4S37.2,40,36.7,40h-14c-0.5,0-1-0.2-1.4-0.6c-0.4-0.4-0.6-0.9-0.6-1.4v-7h2v7h14V24h-7v-2h7c0.5,0,1,0.2,1.4,0.6s0.6,0.9,0.6,1.4V38z M22.7,22v-5h-2v5h-5v2h5v5h2v-5h5v-2H22.7z';
  private dataZoomBackIcon = 'path://M24.5,23.9l2.5,2.5l-1.4,1.4L20.7,23l4.9-4.9l1.4,1.4L24.5,22l12.2,0c0.5,0,1,0.2,1.4,0.6c0.4,0.4,0.6,0.9,0.6,1.4v14c0,0.5-0.2,1-0.6,1.4S37.2,40,36.7,40h-14c-0.5,0-1-0.2-1.4-0.6c-0.4-0.4-0.6-0.9-0.6-1.4v-7h2v7h14V24L24.5,23.9z';
  private tmvPinIcon = 'path://M12.30469 53.32032 Q12.30469 42.96875 17.38281 34.08203 Q22.46094 25.09766 31.05469 19.72657 Q39.84375 14.35547 50 14.35547 Q60.25391 14.35547 68.94531 19.62891 Q77.53906 24.80469 82.61719 33.78907 Q87.69531 42.57813 87.69531 52.83203 Q87.69531 63.08594 81.54297 75.48829 Q75.48828 85.0586 67.67578 95.21485 Q59.96094 105.46875 55.17578 111.03516 Q53.61328 112.89063 50.39062 116.50391 Q49.60938 115.72266 48.14453 114.35547 Q46.77734 112.89063 42.57812 108.4961 Q38.47656 104.00391 34.66797 99.41407 Q30.76172 94.72657 25.87891 88.08594 Q21.19141 81.44532 17.57812 75.19532 Q12.30469 63.57422 12.30469 53.32032 zM28.51562 51.07422 Q28.51562 60.0586 34.76562 66.3086 Q41.11328 72.5586 49.90234 72.5586 Q58.78906 72.5586 65.13672 66.3086 Q71.38672 60.0586 71.38672 51.07422 Q71.38672 42.1875 65.13672 35.9375 Q58.78906 29.6875 49.90234 29.6875 Q41.01562 29.6875 34.76562 35.9375 Q28.51562 42.28516 28.51562 51.07422 z';

  private originalMapMaxHeightStyle: string = '';

  public fetchError: Signal<boolean> = computed(() => {
    return this.crossService.fetchError()
  });

  constructor(
    private pointService: ProfilePathService,
    private crossService: ElevationService,
    private toggleEventService: ToggleEventService,
    private translateService: TranslateService,
    public traceService: TraceService,
    public languageService: LanguageService,
    private _mapService: MapService,
    private _renderer: Renderer2,
    private _browserSniffer: BrowserSnifferService,
    private backEventService: BackEventService
  ) {

    effect(() => {
      // We "read" the signals here to create the dependency
      const isPhone = this._browserSniffer.isPhone();
      const isTablet = this._browserSniffer.isTablet();
      const isDesktop = this._browserSniffer.isDesktop();
      const isLarge = this._browserSniffer.isLargeDesktop();

      // Check if echart is ready
      if (this.echartsInstance) {
        // Logic inside setLabelInterval will now use the current signal values
        this.setLabelInterval();
      }
    });
  }


  ngOnInit(): void {

    // setTimeout(() => {
    //   this.traceService.toggleElevationProfile();
    // }, 2000);

    this._mapElement = this._mapService.getCurrentMap().getTargetElement();
    this._renderer.setStyle(this._mapElement, 'max-height', '100%');

    this.subscriptions = new Subscription();
    this.subscriptions.add(
      this.crossService.$comingPoints.subscribe((points: SegmentPoint[]) => {
        this.updatePoints(points);
        // unfortunately, this is the only (ugly) way to display the final data change :-(
        setTimeout(() => {
          this.updateMergeOptions();
        }, 10);
      })
    );

    this.subscriptions.add(
      this.crossService.$isComputingPoints.subscribe((isBuilding: boolean) => {
        this.isComputingPoints.set(isBuilding);
      })
    );
    this.subscriptions.add(
      this.crossService.$isBuildingProfile.subscribe((isBuilding: boolean) => {
        this.isBuildingProfileFromPoints.set(isBuilding);
      })
    );

    this.subscriptions.add(
      this.crossService.$profileSummary.subscribe(summary => {
        this.summary = summary ? JSON.stringify(summary) : '';
        if (summary && summary.path && typeof summary.path['positive gradient'] === 'number') {
          //console.log('----- summary -----')
          //console.dir(summary);
          this.visibleRouteLength = this.traceService.getRouteLength();
          this.setLabelInterval();
          // this.setLabelInterval(this.visibleRouteLength?this.visibleRouteLength:this.traceService.getRouteLength());
          this.traceService.updateElevationUpDownDist(summary.path['positive gradient'], summary.path['negative gradient']);
        } else {
          //if (this.traceService.isElevationProfileComponentOpen() || this.traceService.canUpdateElevationWhenPanelClosed()) {
          if (summary && summary.error && (this.traceService.isElevationProfileComponentOpen() || this.traceService.canUpdateElevationWhenPanelClosed())) {
            this.traceService.updateElevationUpDownDist(-1, 1);
          }
        }
      })
    );

    this.subscriptions.add(
      this.crossService.$pois.subscribe(pois => this.setPoiData(pois))
    );

    this.subscriptions.add(
      this.traceService.updatedTraceRouteData$.subscribe((traceRouteDataUpdateType: TraceRouteDataUpdateType) => {
        const hasRouteToCreateProfile = this.traceService.hasRouteToCreateProfile();
        this.ref_hasRouteToCreateProfile.set(hasRouteToCreateProfile);
        if (this.ref_hasRouteToCreateProfile()) {
          this._olControlElements?.forEach((elm) => {
            this._renderer.addClass(elm, 'profile-btn-visible');
          });
        } else {
          this._olControlElements?.forEach((elm) => {
            this._renderer.removeClass(elm, 'profile-btn-visible');
          });
        }
      })
    );

    this.subscriptions.add(
      this.pointService.$hoveredProfilePoint.subscribe((pt: SegmentPoint) => {
        this.traceService.addElevationProfilePointToMap(pt, null);
      })
    );

    this.subscriptions.add(
      this.pointService.$clickedProfilePoint.subscribe((pt: SegmentPoint) => {
        this.traceService.centerOrZoomInOut(new Point([pt.getX(), pt.getY()]), false);
      })
    );

    this.subscriptions.add(
      this.pointService.$clickedProfilePointOfInterest.subscribe((poi: ElevationPoiType) => {
        this.traceService.centerOrZoomInOut(new Point([poi.x, poi.y]), true);
        /*
        try {
          let poiIndex = parseInt(poi.label) - 1;
          if (poiIndex >= 0) {
            this.traceService.zoomToWayPoint(poiIndex, null);
          }
        } catch (error) { }
        */
      })
    );

    this.subscriptions.add(
      this.toggleEventService.emitter$.subscribe(
        //adapt map height and width
        (toggleEvent: ToggleEventType) => {
          if (toggleEvent.forwardedFor === "trace") {
            if (toggleEvent.eventName === ToggleEventNameType.CLOSE) {
              this._renderer.removeClass(this._elevationProfileElement, 'trace-drawer-open');
            }
            if (toggleEvent.eventName === ToggleEventNameType.OPEN_COMMAND) {
              if (!this._browserSniffer.isPhone()) {
                this._renderer.addClass(this._elevationProfileElement, 'trace-drawer-open');
              }
            }
          }
          if (toggleEvent.forwardedFor === "legend") {
            if (toggleEvent.eventName === ToggleEventNameType.CLOSE) {
              this._renderer.removeClass(this._elevationProfileElement, 'legend-drawer-open');
            }
            if (toggleEvent.eventName === ToggleEventNameType.OPEN) {
              this._renderer.addClass(this._elevationProfileElement, 'legend-drawer-open');
            }
          }
          if (toggleEvent.forwardedFor === "drawing-tools") {
            if (toggleEvent.eventName === ToggleEventNameType.CLOSE_COMMAND || toggleEvent.eventName === ToggleEventNameType.OPEN_COMMAND) {
              this.isDrawingToolsOpen = toggleEvent.eventName === ToggleEventNameType.OPEN_COMMAND;
            }
            if (toggleEvent.eventName === ToggleEventNameType.CLOSE_COMMAND && this.isChartShown()) {
              // this._renderer.setStyle(this._elevationProfileElement, 'top', '');
              if (this.isChartShown()) {
                // this._renderer.setStyle(this._mapElement, 'height', 'calc(100vh - 234px)');
              }
            }
            // if (toggleEvent.eventName === ToggleEventNameType.OPEN_COMMAND && this.isChartShown()) {
            //   this._renderer.setStyle(this._mapElement, 'height', 'calc(100vh - 234px)');
            //   this._renderer.setStyle(this._elevationProfileElement, 'top', '124px');
            // }
          }
          if (toggleEvent.forwardedFor === "elevation-profile") {
            if (toggleEvent.eventName === ToggleEventNameType.CLOSE_COMMAND || toggleEvent.eventName === ToggleEventNameType.OPEN_COMMAND) {
              this.isChartShown.set(toggleEvent.eventName === ToggleEventNameType.OPEN_COMMAND);
              if (this.isChartShown) {
                this.echartsInstance?.setOption({ tooltip: { show: true } });
              } else {
                //the tooltip sometimes stays after a back event, so forcing it to close
                this.echartsInstance?.setOption({ tooltip: { show: false } });
              }
            }
            if (toggleEvent.eventName === ToggleEventNameType.OPEN_COMMAND) {
              this._renderer.setStyle(this._mapElement, 'bottom', '0px');
              // if (this.isDrawingToolsOpen) {
              // this._renderer.setStyle(this._mapElement, 'height', 'calc(100vh - 296px)');
              // this._renderer.setStyle(this._elevationProfileElement, 'top', '124px');
              // }
              // else {
              // this._renderer.setStyle(this._mapElement, 'height', `${height}px`);//234px
              this.originalMapMaxHeightStyle = this._mapElement.style.maxHeight ? this._mapElement.style.maxHeight : '';
              //moved to css
              // this._renderer.setStyle(this._mapElement, 'max-height', 'calc(100vh - 283px)');//234px 227px
              //this._renderer.setStyle(this._mapElement, 'height', 'calc(100vh - 226px)');//234px
              // }
              this._olControlElements.forEach((elm) => {
                this._renderer.addClass(elm, 'elevation-profile-open');
              });

              this.backEventService.registerOpenedWindow({ windowName: 'elevation-profile', isAutoCloseableWindow: false });
            }
            if (toggleEvent.eventName === ToggleEventNameType.CLOSE_COMMAND) {
              this._renderer.setStyle(this._mapElement, 'bottom', '0px');
              this._renderer.setStyle(this._mapElement, 'max-height', this.originalMapMaxHeightStyle);
              // this._renderer.setStyle(this._elevationProfileElement, 'top', '64px');
              this._olControlElements.forEach((elm) => {
                this._renderer.removeClass(elm, 'elevation-profile-open');
              });
            }
          }
          //not at startup?
          if (toggleEvent.forwardedFor === 'theme-switch') {
            if (toggleEvent.eventName === ToggleEventNameType.DARK_THEME) {
              this.isDarkTheme = true;
            }
            if (toggleEvent.eventName === ToggleEventNameType.LIGHT_THEME) {
              this.isDarkTheme = false;
            }
            this.updateMergeOptions();
          }
        }
      )
    );

    this.subscriptions.add(
      this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
        window.setTimeout(() => {
          //this.registerChartLocale();
          this.updateMergeOptions();
        }, 300);
      })
    );

    // this.subscriptions.add(
    //   this._browserSniffer.subscribeToLayoutChanges().subscribe((e) => {
    //     if (this.echartsInstance) { this.setLabelInterval(); }
    //     //change toolbox
    //     // if(this._browserSniffer.isMobile){   
    //     //this.setToolbox();
    //     // }
    //   })
    // );

    //setting the echarts locale seems only possible at init(?) --> e.g. echarts.registerLocale("FR", langFR), html attribute [initOpts]=="{locale: languageService.selectedLang}"
    //until we find a better (or more correct) solution, we will update language in mergeOptions, when necessary
    this.options = this.getOptions();
  }

  /*
  end init
  */

  ngAfterViewInit() {
    this._elevationProfileElement = document.querySelectorAll<HTMLElement>('.elevationProfile')[0];
    this._olControlElements = document.querySelectorAll<HTMLElement>(
      'body, tt-zoom, .elevation-profile-button-container, .baselayer-selector-button-container, .legend-button-container, .remarks-select-button-container, .trace-select-button-container, .measure-button-container, .ol-scale-line, tt-mouse-position,  tt-mouse-position-mapindex'
    );
    this.isDarkTheme = document.body.classList.contains('ngi-theme-dark');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /* basic conf */
  private getOptions() {
    return {
      // title: {
      //   text: 'Elevation profile',
      // },
      grid: {
        left: '60px',
        right: '60px',
        bottom: '20px'
      },
      xAxis: {
        type: 'category',
        data: this.data.xData,
        boundaryGap: false,
        //name: 'distance',
        // axisTick: {
        //   alignWithLabel: true
        // },
        axisLabel: {
          interval: 39,
          formatter: (value: number): string => {
            let formattedValue = '';
            if (this.distanceUnit === 'm') {
              formattedValue += value;
            } else {
              formattedValue = (Math.round(value / 1000 * 1000) / 1000).toFixed(3).replace(/(0)+$/, '').replace(/\.$/, '');
              // formattedValue = ((Math.round(value / 100) * 100) / 1000).toFixed(1).replace(/(0)+$/, '').replace(/\.$/, '');
            }
            return formattedValue;
          }
        },
        axisPointer: {
          type: 'line',
          axis: 'xAxis',
          snap: false,
          z: 2,
          // label:{
          //   show:true,
          // },
          lineStyle: {
            color: this.ngiBlueColor,
            // color: 'white',
            //to merge !
            // color: this.isDarkTheme ? '#515151' : '#e0e0e0',
            width: 1,
            type: 'solid',
            opacity: 0.5
          },
          // slide handle for mobile:
          // handle: {
          //   show: true,
          //   color: this.ngiBlueColor,
          //   size: 40,
          //   margin: 25
          // },
          //position: [100, 100],
          position: 'bottom',
        },
      },
      yAxis: {
        type: 'value',
        min: (value: any) => {
          return Math.floor(value.min);
        },
        max: (value: any) => {
          return Math.ceil(value.max);
        },
        name: 'm',
        nameGap: 40,
        minInterval: 1000,
        // boundaryGap:true,
        axisLabel: {
          showMinLabel: true,
          showMaxLabel: true,
        },
        // margin for labels
        offset: 10
      },
      dataZoom: [
        {
          type: 'inside',
          // enable pinch zoom
          disabled: false,
          minValueSpan: 9,
        }
      ],
      series: [
        {
          // name: 'elevation',
          type: 'line',
          z: 2,
          data: this.data.yData,
          lineStyle: {
            color: this.ngiOrangeColorVeryDark,
            width: 0.5,
          },
          showSymbol: false,
          smooth: true,
          // silent: true,
          symbol: 'circle',
          symbolSize: 12,
          itemStyle: {
            color: this.ngiBlueColor,
            borderColor: '#fff',
            borderWidth: 2,
          },
          triggerLineEvent: false,
          // emphasis: {
          //   disabled: false,
          //  itemStyle: {
          //   color: this.ngiOrangeColor
          // }
          // },
        },
        {
          name: 'poi',
          type: 'scatter',
          data: this.data.poiData,
          z: 3,
          //silent: true,

        },
        {
          name: 'poi-bar',
          type: 'bar',
          data: this.data.poiData,
          silent: true,
          emphasis: {
            disabled: true,
          },
        },
        {
          name: 'start-end-marker',
          type: 'bar',
          z: 7,
        },
      ],
      tooltip: {
        trigger: 'axis',
        show: true,
        //position: 'top', //only with trigger 'item
        formatter: (params: any) => {
          let display = '';
          const param = params[0];
          const id = this.data.ids[param.dataIndex];
          const x = this.data.lats[param.dataIndex];
          const y = this.data.longs[param.dataIndex];
          const el = this.formatMeasurement(param.value);
          const dist = this.formatMeasurement(this.data.xData[param.dataIndex]);

          this.hoveredPointId = param.dataIndex;

          // formatLength
          // console.log('param.value: ' + param.value)
          // console.log(this.data.xData[param.dataIndex] + ' - dist: ' + dist)
          // const slope = this.data.slopes[param.dataIndex];
          // const gradient = this.data.gradients[param.dataIndex];
          // const totalGradient = Math.round((gradient[0] + gradient[1]) * 10) / 10;

          this.pointService.onHoverProfilePoint(new SegmentPoint(id, x, y, el, dist));

          // display += "id : " + id
          //   + "<br> x : " + x
          //   + "<br> y : " + y
          //   + "<br> " + this.translateService.instant("elevation-profile.elevation") + " : " + el + "m<br> "
          //   + "<br> " + this.translateService.instant("elevation-profile.slope") + " : " + slope + "%"
          //   + "<br> " + this.translateService.instant("elevation-profile.distance") + " : " + dist + "m"
          //   + "<br> " + this.translateService.instant("elevation-profile.gradient") + " : " + (totalGradient) + " (+" + gradient[0] + ", " + gradient[1] + ")"
          //   + "<br> ";
          // display += this.translateService.instant("elevation-profile.gradient") + " : " + (totalGradient) + " (+" + gradient[0] + ", " + gradient[1] + ")";
          display += this.translateService.instant("elevation-profile.elevation") + " : " + el + "<br> "
            + this.translateService.instant("elevation-profile.distance") + " : " + dist;
          //  + "<br> " + this.translateService.instant("elevation-profile.gradient") + " : " + (totalGradient) + " (+" + gradient[0] + ", " + gradient[1] + ")";

          //pve temp disable for this version?
          // if (params.length > 1) {
          //   // a point of interest is present
          //   const poiVal = params[1].value;
          //   console.dir(params)
          //   const poi: any = {
          //     x: poiVal[0],
          //     y: poiVal[1],
          //     label: poiVal[2],
          //   }
          //   this.pointService.activateMapPoint(poi);

          //   //  this.pointService.onClickProfilePointOfInterest(poi);
          //   // this.pointService.$activatedProfilePointOfInterest.subscribe(point=>console.dir(point))
          //   // this.pointService.activateProfilePointOfInterest
          //   display = "<hr>" + poi.label + "<p>Hi Peter ! You can display here every properties from ElevationPoiType (see elevation-poi.ts) "
          //     + "<br> You can also subscribe to $activatedProfilePointOfInterest (ProfilePathService) to trigger what you want when this is shown.  Enjoy !";
          // }

          return display;
        },
        appendTo: 'body'
      },
      toolbox: {
        feature: {
          dataZoom: {
            // show: true,
            show: (this._browserSniffer.isTouchDevice) ? false : true,
            icon: {
              zoom: this.dataZoomIcon,
              /* hide back btn with fake icon value */
              back: 'noback'
            },
            iconStyle: {
              color: this.ngiGrayColor,
              borderWidth: 0,
            },
            emphasis: {
              iconStyle: {
                color: this.ngiOrangeColor
              }
            },
          },
          //custom tool, name has to start with 'my', restores the view
          myRestore: {
            //show: (this._browserSniffer.isTouchDevice) ? true : false,
            show: true,
            //title: 'Restore zoom',
            icon: this.myRestoreIcon,
            iconStyle: {
              color: this.ngiGrayColor,
              // borderColor: "#fffff",
              borderWidth: 0
            },
            onclick: () => {
              // this.sjappi()
              this.echartsInstance?.dispatchAction(
                {
                  type: 'dataZoom',
                  start: 0,
                  end: 100
                });
            },
            emphasis: {
              iconStyle: {
                color: (this._browserSniffer.isTouchDevice) ? "#000000" : this.ngiOrangeColor,
              }
            },
          },
        },
        right: 90,
        top: 5,
        itemGap: 5,
        // iconStyle: {
        //   color: this.ngiGrayColor,
        //   // borderColor: "#fffff",
        //   borderWidth: 0
        // },
        itemSize: (this._browserSniffer.isTouchDevice) ? 27 : 20,
      },
      animation: false,
      textStyle: {
        fontFamily: 'Source Sans Pro'
      }
    };
  }

  private setPoiData(pois: any) {

    /*
    if (!pois && this.echartsInstance) {
      const option = this.echartsInstance.getOption();
      option.series[1].data = [];
      option.series[2].data = [];
      this.echartsInstance.setOption(option);
      return
    }
    */

    this.data.poiData = pois ? pois.poisData : [];
    this.maxElevation = pois ? pois.maxElevation : 0;

    this.mergeOptions.set({
      series: [
        {
          data: this.data.poiData,
          name: 'poi',
          type: 'scatter',
          z: 11,
          symbolSize: 22,
          //symbolOffset: [0, -20],
          color: this.ngiBlueGreenColor,
          opacity: 1.0,
          itemStyle: {
            opacity: 1.0,
            borderWidth: 2,
            borderColor: '#ffffff'
          },
          label: {
            show: true,
            position: 'inside',
            color: '#fff',
            fontWeight: 600,
            formatter: function (d) {
              //if label is available, take label, otherwise id
              return d.data[3] ?  d.data[3] : d.data[2];
            },
            backgroundColor: this.ngiBlueGreenColor,
            borderColor: '#ffffff',
            borderWidth: 2,
            borderRadius: 11,
            padding: 0,
            width: 22,
            height: 22
          },
          emphasis: {
            // scale: 3,
            label: {
              backgroundColor: this.ngiBlueColor,
              borderRadius: 13,
              width: 26,
              height: 26
            }
          }
        },
        //connecting lines to poi's
        {
          data: this.data.poiData,
          name: 'poi-bar',
          type: 'bar',
          barWidth: 0.5,
          opacity: 0,
          z: 3,
          silent: true,
          itemStyle: {
            color: 'transparent',
            borderColor: this.ngiBlueGreenColor,
            borderWidth: 1,
            borderType: [2, 2],
            opacity: 0.25
          }
          //color: this.ngiOrangeColorVeryDark,
        },
      ]
    });
  }


  /**
   * we use this function to update the graph data, but also to update the graph labels (i8n) because these are not managed by angular
   */

  private updateMergeOptions() {
    // console.log('update merge options ');
    // if (end === 100 && start === 0) {
    //   this.setMyRestoreBtn(this.isDarkTheme? this.ngiDarkGrayColor:this.ngiGrayColor);
    // } else {
    //   this.setMyRestoreBtn(this.isDarkTheme? this.ngiGrayColor:this.ngiDarkBlueColor);
    // }
    //this.echartsInstance?.getOption().dataZoom[0].endValue

    const isForPdf = document.getElementById('elevationProfile').classList.contains('pdfRendering');
    //no main title for pdf?
    //other line color for pdf?
    //shorter axis names for pdf? km would be better then m
    //no gradient for pdf?


    let color = this.isZoomed ? (this.isDarkTheme ? this.ngiGrayColor : this.ngiDarkBlueColor) : ((this._browserSniffer.isPhone() ? 'transparent' : (this.isDarkTheme ? this.ngiDarkGrayColor : this.ngiGrayColor)));//this.ngiDarkGrayColor this.ngiGrayColor
    this.setMyRestoreBtn(color);

    const newMergeOptions = {
      // title: { text: isForPdf ? ' ' : this.translateService.instant('elevation-profile.title') },
      backgroundColor: isForPdf ? 'rgba(255, 255, 255, 1.0)' : this.isDarkTheme ? 'rgba(17, 16, 17, 1)' : 'rgba(255, 255, 255, 1)',
      color: [isForPdf ? 'rgba(234, 89, 1, 1)' : this.isDarkTheme ? 'rgba(234, 89, 1, 1)' : 'rgba(234, 89, 1, 1)'],// 'black'
      textStyle: {
        color: isForPdf ? 'black' : this.isDarkTheme ? 'white' : 'black'
      },
      xAxis: {
        data: this.data.xData,
        //without '\r\n' the text is othen outside the canvas
        // name: isForPdf ? '' + this.distanceUnit : this.translateService.instant('elevation-profile.distance') + '\r\n(' + this.distanceUnit + ')'
        name: this.distanceUnit,
      },
      yAxis: {
        // name: isForPdf ? 'm' : this.translateService.instant('elevation-profile.elevation') + ' (m)',
        //name: 'm',      
        splitLine: {
          show: false,
        },
      },
      series: [
        {
          data: this.data.yData,
          name: isForPdf ? 'm' : this.translateService.instant('elevation-profile.elevation') + ' (m)',
          // showSymbol: !isForPdf,
          //showSymbol: false,
          lineStyle: {
            color: isForPdf ? this.ngiOrangeColorDarker : this.ngiOrangeColorVeryDark,
            width: isForPdf ? 2 : 0.5,
          },

          areaStyle: isForPdf ?
            ({
              color: '#fff',
              opacity: 1.0
            })

            : ({
              color: new graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0.5,
                  color: this.ngiOrangeColor
                },
                {
                  offset: 1.0,
                  color: this.ngiOrangeColorDarker
                }
              ]),
              opacity: 1.0
            }),
          emphasis: {
            disabled: true
          }
        },
        {
          name: 'start-end-marker',
          type: 'bar',
          barWidth: 0.5,
          z: 11,
          data:
            [
              {
                value: [0, this.maxElevation]
              },
              {
                value: [this.data.yData.length - 1, this.maxElevation],
                itemStyle: {
                  color: this.isDarkTheme ? this.ngiGrayColor : this.ngiDarkBlueColor
                }
              }
            ],
          color: this.ngiGreenBlueColor,
          markPoint: {
            symbol: this.tmvPinIcon,//'pin',
            symbolOffset: [0, -20],
            data: [
              //starting point pin with border behind
              {
                name: 'startMarkerPointBorder',
                coord: [0, this.maxElevation],
                itemStyle: {
                  borderWidth: 3,
                  borderColor: '#fff',
                }
              },
              {
                name: 'startMarkerPoint',
                coord: [0, this.maxElevation],
              },
              //end point pin with border behind
              {
                name: 'endMarkerPointBorder',
                coord: [this.data.yData.length - 1, this.maxElevation],
                itemStyle: {
                  borderWidth: 3,
                  borderColor: '#fff',
                },
              },
              {
                name: 'endMarkerPoint',
                coord: [this.data.yData.length - 1, this.maxElevation],
                itemStyle: {
                  color: this.ngiDarkBlueColor
                },

              },
            ],
            symbolKeepAspect: true,
            symbolSize: 26,
            emphasis: {
              disabled: true,
            },
          },
        },
      ],
      tooltip: {
        backgroundColor: this.isDarkTheme ? 'rgb(67,67,67)' : 'rgb(255,255,255)',
        borderColor: this.isDarkTheme ? 'rgb(67,67,67)' : 'rgb(255,255,255)',
        textStyle: {
          color: this.isDarkTheme ? 'rgb(255,255,255)' : 'rgb(50,50,50)',
        }
      }
    };

    if (isForPdf) {
      //toolbox icons not shown in pdf
      newMergeOptions['toolbox'] = {
        show: false
      };
    } else {
      if (this.echartLocale == this.languageService.selectedLang()) {
        // newMergeOptions['toolbox'] = {
        //   show: true
        // };
      } else {
        this.echartLocale = this.languageService.selectedLang();
        const chartText = this.getChartText();
        newMergeOptions['toolbox'] = {
          // show: true,
          feature: {
            dataZoom: {
              title: {
                zoom: chartText.toolbox.dataZoom.title.zoom,
                //back: chartText.toolbox.dataZoom.title.back
              }
            },
            myRestore: {
              //show: (this._browserSniffer.isTouchDevice) ? true : false,
              title: chartText.toolbox.dataZoom.title.back,
            },
          }
        };
      }
    }
    this.mergeOptions.set(newMergeOptions);
  }

  // private setToolbox() {
  //   this.echartsInstance?.setOption(
  //     {
  //       toolbox: {
  //         // show: true,
  //         feature: {
  //           dataZoom: {
  //             show: (this._browserSniffer.isTouchDevice) ? false : true,
  //             // yAxisIndex: 'none',
  //             icon: {
  //               zoom: this.dataZoomIcon,
  //               back: this.dataZoomBackIcon
  //             },

  //             iconStyle: {
  //               color: this.ngiGrayColor,
  //               borderWidth: 0,
  //             }
  //           },
  //           myRestore: {
  //             show: this._browserSniffer.isTouchDevice ? true : false,
  //             // show: (this._browserSniffer.isMobile) ? true : false,
  //             title: 'custom extension method 1',
  //             // icon: 'path://M432.45,595.444c0,2.177-4.661,6.82-11.305,6.82c-6.475,0-11.306-4.567-11.306-6.82s4.852-6.812,11.306-6.812C427.841,588.632,432.452,593.191,432.45,595.444L432.45,595.444z M421.155,589.876c-3.009,0-5.448,2.495-5.448,5.572s2.439,5.572,5.448,5.572c3.01,0,5.449-2.495,5.449-5.572C426.604,592.371,424.165,589.876,421.155,589.876L421.155,589.876z M421.146,591.891c-1.916,0-3.47,1.589-3.47,3.549c0,1.959,1.554,3.548,3.47,3.548s3.469-1.589,3.469-3.548C424.614,593.479,423.062,591.891,421.146,591.891L421.146,591.891zM421.146,591.891',
  //             icon: this.myRestoreIcon,
  //             onclick: () => {
  //               // this.sjappi()
  //               this.echartsInstance?.dispatchAction(
  //                 {
  //                   type: 'dataZoom',
  //                   start: 0,
  //                   end: 100
  //                 });
  //             }
  //           },
  //           iconStyle: {
  //             color: '#ff6600',
  //             // borderColor: "#ffffff",
  //             // borderWidth: 2
  //           },
  //         },
  //         right: 90,

  //         emphasis: {
  //           iconStyle: {
  //             borderColor: "#000080"
  //           }
  //         },
  //         itemSize: (this._browserSniffer.isTouchDevice) ? 35 : 20,
  //       },
  //     },
  //     //to avoid error with datazoom
  //     {
  //       replaceMerge: ['toolbox']
  //     }
  //   )
  // }


  setLabelInterval() {
    const tick_intervals = [0, 1, 3, 3, 9, 19, 39, 99, 199, 399, 399, 999, 1999, 19999, 39999];
    const intervals = [100, 100, 100, 500, 500, 1000, 5000, 5000, 10000, 50000, 50000, 50000, 50000, 50000, 50000];
    const routeLengths = [0, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000, 2500000, 10000000, Infinity];

    let interval: number = 1000; // default
    let tickInterval: number = 19;

    for (let i = 0; i < routeLengths.length; i++) {
      if (this.between(this.visibleRouteLength, routeLengths[i], routeLengths[i + 1])) {

        if (this._browserSniffer.isPhone()) {
          interval = (intervals[i] >= 999) ? intervals[i] : intervals[i + 1];
          tickInterval = tick_intervals[i];
        } else {
          // Tablet, Desktop, and Large Desktop
          interval = intervals[i];
          tickInterval = tick_intervals[i];
        }
        break;
      }
    }

    this.echartsInstance?.setOption({
      xAxis: {
        axisTick: { interval: tickInterval, alignWithLabel: true },
        axisLabel: {
          interval: (index: number, value: number) => value % interval === 0
        }
      }
    });
  }

  setLabelInterval_old() {
    //console.log('\\\/// set label interval... \\\/// ' + this.visibleRouteLength);
    // supposing this.crossService.standardDistance is 25!
    // each  25m,50m,100m,125m,250m,500m,1km,2.5km,5km,10km,50km, 100km
    // let intervals: number[] = [0, 1, 3, 4, 9, 19, 39, 99, 199, 399, 999, 1999, 3999, 19999, 39999]
    // let tick_intervals: number[] = [0, 1, 3, 3, 9, 19, 39, 99, 199, 399, 999, 1999, 3999, 19999, 39999]
    // let intervals: number[] = [3, 3, 19, 19, 19, 39, 39, 99, 199, 399, 999, 1999, 3999, 19999, 39999]
    // 5000 5km 1000 1km
    let tick_intervals: number[] = [0, 1, 3, 3, 9, 19, 39, 99, 199, 399, 399, 999, 1999, 19999, 39999];
    let intervals: number[] = [100, 100, 100, 500, 500, 1000, 5000, 5000, 10000, 50000, 50000, 50000, 50000, 50000, 50000]
    let routeLengths: number[] = [0, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000, 250000, 500000, 1000000, 2500000, 10000000, Infinity]
    let interval: number;
    let tickInterval: number;
    for (let i = 0; i < routeLengths.length; i++) {
      if (this.between(this.visibleRouteLength, routeLengths[i], routeLengths[i + 1])) {
        // console.log('in between ' + routeLengths[i] + ' and ' + routeLengths[i + 1]);
        if (this._browserSniffer.isPhone()) {
          interval = (intervals[i] >= 999) ? intervals[i] : intervals[i + 1];
          tickInterval = tick_intervals[i];
          // console.log(i + ' mobile ' + tickInterval)
          // console.log(i + ' mobile ' + interval)
        }
        else if (this._browserSniffer.isTablet) {
          interval = intervals[i];
          tickInterval = tick_intervals[i];
          // console.log('tablet ' + tickInterval)
          // console.log('tablet ' + interval)
        }
        else if (this._browserSniffer.isLargeDesktop) {
          interval = intervals[i];
          tickInterval = tick_intervals[i];
          // console.log('isLargeDesktop ' + tickInterval)
          // console.log('isLargeDesktop ' + interval)
        }
        else if (this._browserSniffer.isDesktop) {
          interval = intervals[i];
          tickInterval = tick_intervals[i];
          // console.log('isDesktop ' + tickInterval)
          // console.log('isDesktop ' + interval)
        }
        break;
      }
    }
    this.echartsInstance?.setOption({
      xAxis: {
        axisTick: { interval: tickInterval, alignWithLabel: true },
        // axisLabel: { interval: interval }
        axisLabel:
        {
          interval: (index: number, value: number) => {
            if (value % interval === 0) {
              return true
            }
          }
        }
      }
    });
  }

  private between(x: number, min: number, max: number) {
    return x >= min && x <= max;
  }

  private formatMeasurement(measurement: number) {
    let output;
    if (measurement > 1000) {
      output = (Math.round(measurement / 1000 * 100) / 100) + ' ' + 'km';
    } else {
      output = (Math.round(measurement * 10) / 10) + ' ' + 'm';
    }
    return output;
  }

  private getChartText() {

    let chartText = langEN;
    switch (this.languageService.selectedLang().toUpperCase()) {
      case "FR": {
        chartText = langFR;
        break;
      }
      case "DE": {
        chartText = langDE;
        break;
      }
      case "NL": {
        //NL is not available, update the parts that we will use
        const langNL = JSON.parse(JSON.stringify(langEN));
        // langNL.toolbox.dataView.title = 'Data Ovezicht';
        // langNL.toolbox.dataView.lang = ['Data Ovezicht', 'Sluiten', 'Verversen'];
        langNL.toolbox.dataZoom.title.zoom = 'Zoom';
        langNL.toolbox.dataZoom.title.back = 'Zoom Verwijderen';
        // langNL.toolbox.saveAsImage.title = 'Opslaan als foto';
        // langNL.toolbox.saveAsImage.lang = ['Rechtsklikken om foto op te slaan'];
        chartText = langNL;
        break;
      }
    }

    return chartText;
  }

  /*
  private registerChartLocale() {
    //EN is available by default
    switch (this.languageService.()).toUpperCase()) {
      case 'FR': {
        echarts.registerLocale('FR', langFR);
        break;
      }
      case 'DE': {
        echarts.registerLocale('DE', langDE);
        break;
      }
      case "NL": {
        //NL is not available, update the parts that we will use
        const langNL = JSON.parse(JSON.stringify(langEN));
        langNL.toolbox.dataView.title = 'Data Ovezicht';
        langNL.toolbox.dataView.lang = ['Data Ovezicht', 'Sluiten', 'Verversen'];
        langNL.toolbox.dataZoom.title.zoom = 'Zoom';
        langNL.toolbox.dataZoom.title.back = 'Zoom Verwijderen';
        langNL.toolbox.saveAsImage.title = 'Opslaan als foto';
        langNL.toolbox.saveAsImage.lang = ['Rechtsklikken om foto op te slaan'];
        echarts.registerLocale('NL', langNL);
        break;
      }
    }
  }
  */

  onChartInit(ec: ECharts) {
    this.echartsInstance = ec;
    this.echartsInstance.resize()
    this.subscriptions.add(
      this.pointService.$activatedMapPoint.subscribe(pt => {
        if (typeof this.selectedPointId === 'number') {
          this.echartsInstance.dispatchAction({ type: 'downplay', dataIndex: this.selectedPointId });
          this.selectedPointId = null;
        }
        if (pt) {
          //is the graph zoomed in and is the route position outside the graph?
          if (pt.getId() < this.echartsInstance.getOption().dataZoom[0].startValue) {
            let diff = 5;
            while (pt.getId() < (this.echartsInstance.getOption().dataZoom[0].startValue - diff)) {
              diff += 5;
            }
            while ((this.echartsInstance.getOption().dataZoom[0].startValue - diff) < 0) {
              diff--;
            }
            this.echartsInstance.dispatchAction({ type: 'dataZoom', startValue: (this.echartsInstance.getOption().dataZoom[0].startValue - diff), endValue: (this.echartsInstance.getOption().dataZoom[0].endValue - diff) });
          } else if (pt.getId() > this.echartsInstance.getOption().dataZoom[0].endValue) {
            let diff = 5;
            while (pt.getId() > (this.echartsInstance.getOption().dataZoom[0].endValue + diff)) {
              diff += 5;
            }
            /*
            endValue is not always prefectly 100
            while((this.echartsInstance.getOption().dataZoom[0].endValue + diff) > 100) {
              diff--;
            }
            */
            if ((this.echartsInstance.getOption().dataZoom[0].endValue + diff) > 100) {
              diff = 100 + (pt.getId() - 100);
            }
            this.echartsInstance.dispatchAction({ type: 'dataZoom', startValue: (this.echartsInstance.getOption().dataZoom[0].startValue + diff), endValue: (this.echartsInstance.getOption().dataZoom[0].endValue + diff) });
          }

          this.echartsInstance.dispatchAction({ type: 'highlight', dataIndex: pt.getId() });
          this.echartsInstance.dispatchAction({
            type: 'showTip',
            seriesIndex: 0,
            dataIndex: pt.getId()
          });
          this.selectedPointId = pt.getId();
        } else {
          this.echartsInstance.dispatchAction({ type: 'hideTip' });
        }
      })
    );
    ec.on('click', 'series', (params) => {
      const seriesType = Array.isArray(params) && params.length > 0 ? params[0].seriesType : params.seriesType;
      const dataIndex = Array.isArray(params) && params.length > 0 ? params[0].dataIndex : params.dataIndex;
      const seriesValue = Array.isArray(params) && params.length > 0 ? params[0].value : params.value;
      switch (seriesType) {
        case 'scatter': {
          const poiValue = this.data.poiData[dataIndex];
          const poi = {
            closestPointId: poiValue[0],
            closestPointElevation: poiValue[1],
            id: poiValue[2],
            label: poiValue[3],
            x: poiValue[4],
            y: poiValue[5],
          };
          this.pointService.onClickProfilePointOfInterest(poi)
          break;
        }
        case 'line': {
          const id = this.data.ids[dataIndex];
          const x = this.data.lats[dataIndex];
          const y = this.data.longs[dataIndex];
          const el = seriesValue;
          const dist = this.data.xData[dataIndex];
          this.pointService.onClickProfilePoint(new SegmentPoint(id, x, y, el, dist));
          break;
        }
      }

      if (params.name === "endMarkerPoint" || params.name === "endMarkerPointBorder") {
        this.traceService.zoomToWayPoint('endPoint', null, true);
      }
      if (params.name === "startMarkerPoint" || params.name === "startMarkerPointBorder") {
        this.traceService.zoomToWayPoint('startPoint', null, true);
      }
    });


    ec.getZr().on('click', (event) => {
      if (event && event.target && event.target.type === 'ec-polygon' && event.topTarget && event.topTarget.type === 'Line') {
        if (typeof this.hoveredPointId === 'number') {
          this.pointService.onClickProfilePoint(
            new SegmentPoint(this.data.ids[this.hoveredPointId],
              this.data.lats[this.hoveredPointId],
              this.data.longs[this.hoveredPointId],
              //el is not necessary
              0,
              //dist is not necessary
              0));
        }
      }
    });

    ec.on('datazoom', (params: any) => {
      const start = (params.batch) ? params.batch[0]?.start : params.start;
      const end = (params.batch) ? params.batch[0]?.end : params.end;
      const startValue = (params.batch) ? params.batch[0]?.startValue : '';
      const endValue = (params.batch) ? params.batch[0]?.endValue : '';
      //let routeLength// = this.traceService.getRouteLength();
      //end-start percentage of length
      if (end) {
        // console.log('end ' + end + ' start' + start)
        this.visibleRouteLength = (end === 100 && start == 0) ? (this.traceService.getRouteLength()) : ((end * this.traceService.getRouteLength() / 100) - (start * this.traceService.getRouteLength() / 100));
        //console.log('result? ' + this.visibleRouteLength + ' **** * **    *       * ' + ((end * this.visibleRouteLength / 100) - (start * this.visibleRouteLength / 100)));
        if (end === 100 && start === 0) {
          this.isZoomed = false;
          this.setMyRestoreBtn(this.isDarkTheme ? this.ngiDarkGrayColor : (this._browserSniffer.isPhone() ? 'transparent' : this.ngiGrayColor)); //
        } else {
          this.isZoomed = true;
          this.setMyRestoreBtn(this.isDarkTheme ? this.ngiGrayColor : this.ngiDarkBlueColor);
        }
      } else {
        this.visibleRouteLength = (endValue * this.crossService.standardDistance - startValue * this.crossService.standardDistance);
      }
      this.setLabelInterval();
    });
  }
  setMyRestoreBtn(color) {
    this.echartsInstance?.setOption(
      {
        toolbox: {
          feature: {
            myRestore: {
              iconStyle: {
                color: color,
              },
            },
          },
        },
      },
      //to avoid error with datazoom
      // {
      //   replaceMerge: ['toolbox']
      // }
    )
  }

  updatePoints(points: SegmentPoint[]) {
    if (points === null || this.data.ids.length != points.length) {
      this.data.xData = [];
      this.data.yData = [];
      this.data.lats = [];
      this.data.longs = [];
      this.data.ids = [];
      this.data.slopes = [];
      this.data.gradients = [];
    }

    if (points === null) {
      /*
      this.data.xData = [];
      this.data.yData = [];
      this.data.lats = [];
      this.data.longs = [];
      this.data.ids = [];
      */

      this.distanceUnit = 'm'

      if (this.autoCloseOnNoData && this.isChartShown()) {
        console.log('autoclose on no data')
        this.close();
      }

      return;
    }
    points.forEach((pt: SegmentPoint) => {
      this.data.xData[pt.getId()] = Math.round(pt.getDistanceFromStart());
      this.data.yData[pt.getId()] = pt.getElevation();
      this.data.lats[pt.getId()] = pt.getX();
      this.data.longs[pt.getId()] = pt.getY();
      this.data.ids[pt.getId()] = pt.getId();
      // round gradients to decimal
      const positiveGradient = Math.round(pt.getGradient()[0] * 10) / 10;
      const negativeGradient = Math.round(pt.getGradient()[1] * 10) / 10;
      this.data.gradients[pt.getId()] = [positiveGradient, negativeGradient];
      this.data.slopes[pt.getId()] = pt.getSlope();
    });
    this.distanceUnit = !this.autoDistanceUnit || points[points.length - 1].getDistanceFromStart() < 1000 ? 'm' : 'km'
    if (this.autoOpenOnData && !this.isChartShown()) {
      this.isChartShown.set(true);
      this.traceService.toggleElevationProfile();
    }
  }

  close() {
    this.hideTooltip();
    this.isChartShown.set(false);
    this.traceService.toggleElevationProfile();
  }

  hideTooltip() {
    this.echartsInstance?.dispatchAction({
      type: 'hideTip'
    })
  }

  retryAfterFetchElevationsError() {
    if (this.fetchError()) {
      this.crossService.fetchElevations();
    }
  }
}
